import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import type { FilaVista, OpcionMotivo } from '../types/metas.types';
import { ETIQUETA_MOTIVO } from '../types/metas.types';
import { distintivoSuplente, textoAntiguedad } from '../utils/distintivos';
import { pesosEnteros } from '../utils/formato';
import { fechaCorta } from '../utils/semanas';
import { CLASE_OVERLAY } from './ui/clases';

interface Props {
  fila: FilaVista | null;
  /** Para nombrar el motivo de la semana anterior (incluye los históricos). */
  motivos?: OpcionMotivo[];
  onHide: () => void;
}

/**
 * «Historial» del menú ⋮ de una persona: lo que la pantalla ya sabe de ella
 * para decidir su meta —lo de la semana anterior, su venta promedio, su
 * antigüedad y su programa de suplente—, sin pedir nada más a la API. La
 * bitácora de la TIENDA (quién guardó, envió, autorizó) está en Autorización.
 */
export const HistorialColaboradorDialog: React.FC<Props> = ({ fila, motivos = [], onHide }) => {
  const f = fila;
  const etiquetaMotivo = (v: string) =>
    motivos.find((m) => m.valor === v)?.etiqueta ?? ETIQUETA_MOTIVO[v as keyof typeof ETIQUETA_MOTIVO] ?? v;

  const semanaAnterior = (): string => {
    if (!f) return '—';
    if (f.metaSemanaAnterior !== null && f.metaSemanaAnterior !== undefined) return `Meta de ${pesosEnteros(f.metaSemanaAnterior)}`;
    if (f.motivoSemanaAnterior)
      return `Sin meta · ${etiquetaMotivo(f.motivoSemanaAnterior)}${f.detalleSemanaAnterior ? ` (${f.detalleSemanaAnterior})` : ''}`;
    return 'Sin meta ni motivo';
  };

  const venta = (): string => {
    if (!f) return '—';
    const v = f.ventaPromedio !== undefined ? f.ventaPromedio : f.ventaRefSemanal;
    if (v === null || v === undefined) return '— (menos de 2 semanas con venta)';
    const semanas = f.ventaPromedioSemanas;
    return `${pesosEnteros(v)} por semana${semanas ? ` · ${semanas} semana(s) cerradas, en todas sus tiendas` : ''}`;
  };

  const suplente = f ? distintivoSuplente(f.suplente) : null;

  const datos: [string, string][] = f
    ? [
        ['Semana anterior', semanaAnterior()],
        ['Venta semanal promedio (sin IVA)', venta()],
        ['Puesto', `${f.puesto?.trim() || '—'}${f.puestoFuente === 'proscai' ? ' (del ERP)' : ''}`],
        ['Alta', fechaCorta(f.fechaAlta)],
        // Las semanas de cada etapa vienen de la API con la regla de esa semana: desde
        // la S45 de 2026 ya no hay garantía (21-oct-2026, decisión del usuario).
        ['Antigüedad', textoAntiguedad(f.antiguedad)],
        ['Estado en BUK', f.estadoBuk?.trim() || '—'],
        ['Suplente en desarrollo', suplente ? `${suplente.texto}. ${suplente.detalle ?? ''}`.trim() : 'No'],
        ['Apoyo esta semana', f.apoyo ? `${f.apoyo.chain} ${f.apoyo.num}${f.apoyo.tienda ? ` · ${f.apoyo.tienda}` : ''} · ${f.apoyo.dias} día(s)` : 'No'],
      ]
    : [];

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      header={f ? `Historial de ${f.nombre}` : 'Historial'}
      visible={!!f}
      onHide={onHide}
      modal
      className={CLASE_OVERLAY}
      style={{ width: '34rem', maxWidth: '95vw' }}
      footer={<Button label="Cerrar" onClick={onHide} outlined />}
    >
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
        {datos.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="font-semibold text-metas-texto-2">{k}</dt>
            <dd className="num m-0 text-metas-texto">{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Dialog>
  );
};

export default HistorialColaboradorDialog;
